import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Package, AlertTriangle, TrendingUp, TrendingDown, Plus, RefreshCw, Loader2, Trash2, Boxes } from "lucide-react";
import { toast } from "sonner";

const TIPO_LABELS = {
  entrada: { label: "Entrada", color: "text-emerald-400 bg-emerald-500/10 border-emerald-700/40", icon: TrendingUp },
  salida:  { label: "Salida",  color: "text-rose-400 bg-rose-500/10 border-rose-700/40", icon: TrendingDown },
  ajuste:  { label: "Ajuste",  color: "text-amber-400 bg-amber-500/10 border-amber-700/40", icon: RefreshCw },
  merma:   { label: "Merma",   color: "text-zinc-400 bg-zinc-700/30 border-zinc-700",     icon: TrendingDown },
};

export default function Almacen() {
  const [tab, setTab] = useState("alertas");
  const [resumen, setResumen] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewMov, setShowNewMov] = useState(false);
  const [filterMat, setFilterMat] = useState("__all__");
  const [filterTipo, setFilterTipo] = useState("__all__");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/almacen/resumen"),
        api.get("/almacen/alertas"),
        api.get("/materiales"),
      ]);
      setResumen(r1.data);
      setAlertas(r2.data?.alertas || []);
      setMateriales(r3.data || []);
    } catch (err) {
      toast.error("Error cargando almacén");
    } finally { setLoading(false); }
  };

  const loadMovs = async () => {
    const params = {};
    if (filterMat !== "__all__") params.material_id = filterMat;
    if (filterTipo !== "__all__") params.tipo = filterTipo;
    const { data } = await api.get("/almacen/movimientos", { params });
    setMovimientos(data || []);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (tab === "movimientos") loadMovs(); }, [tab, filterMat, filterTipo]);

  const materialesConStock = useMemo(
    () => materiales.filter((m) => m.controla_stock),
    [materiales]
  );

  return (
    <div className="space-y-6" data-testid="almacen-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Boxes className="w-7 h-7 text-amber-400" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Almacén</h1>
            <p className="text-sm text-zinc-500">Control de stock, movimientos y alertas por material.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadAll} disabled={loading}
                  className="bg-transparent border-zinc-700">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2 hidden sm:inline">Refrescar</span>
          </Button>
          <Button onClick={() => setShowNewMov(true)}
                  data-testid="almacen-new-mov"
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Nuevo movimiento
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi label="Productos con stock" value={resumen?.total_productos_con_stock ?? "—"}
             icon={Package} color="text-amber-400" />
        <Kpi label="En alerta"
             value={resumen?.alertas_count ?? "—"}
             icon={AlertTriangle}
             color={resumen?.alertas_count > 0 ? "text-rose-400" : "text-emerald-400"} />
        <Kpi label="Valor a coste"
             value={resumen ? `${(resumen.valor_total_coste || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })} €` : "—"}
             icon={TrendingDown} color="text-zinc-300" />
        <Kpi label="Valor a venta"
             value={resumen ? `${(resumen.valor_total_venta || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })} €` : "—"}
             icon={TrendingUp} color="text-emerald-400" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800">
        <TabBtn active={tab === "alertas"} onClick={() => setTab("alertas")}
                testid="almacen-tab-alertas">
          <AlertTriangle className="w-4 h-4 mr-1.5" /> Alertas
          {alertas.length > 0 && (
            <span className="ml-2 text-xs bg-rose-500/20 text-rose-300 px-1.5 rounded">{alertas.length}</span>
          )}
        </TabBtn>
        <TabBtn active={tab === "movimientos"} onClick={() => setTab("movimientos")}
                testid="almacen-tab-movs">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Movimientos
        </TabBtn>
      </div>

      {/* Alertas */}
      {tab === "alertas" && (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          {alertas.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              <Package className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
              {materialesConStock.length === 0
                ? "Aún no hay materiales con control de stock activado. Actívalo en cada material."
                : "Todo el stock está por encima del mínimo. 👌"}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase">
                <tr>
                  <th className="text-left p-3">Material</th>
                  <th className="text-left p-3 w-32">Ubicación</th>
                  <th className="text-right p-3 w-28">Stock actual</th>
                  <th className="text-right p-3 w-28">Mínimo</th>
                  <th className="text-right p-3 w-32">Déficit</th>
                  <th className="text-left p-3 w-20">Unidad</th>
                </tr>
              </thead>
              <tbody>
                {alertas.map((a) => {
                  const deficit = (a.stock_minimo || 0) - (a.stock_actual || 0);
                  return (
                    <tr key={a.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/30">
                      <td className="p-3 font-medium">{a.nombre}</td>
                      <td className="p-3 text-zinc-400 text-xs">{a.ubicacion_almacen || "—"}</td>
                      <td className="p-3 text-right font-mono text-rose-400 font-semibold">
                        {(a.stock_actual || 0).toFixed(2)}
                      </td>
                      <td className="p-3 text-right font-mono text-zinc-400">
                        {(a.stock_minimo || 0).toFixed(2)}
                      </td>
                      <td className="p-3 text-right font-mono text-rose-400">
                        −{deficit.toFixed(2)}
                      </td>
                      <td className="p-3 text-zinc-400 text-xs">{a.unidad || "m²"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Movimientos */}
      {tab === "movimientos" && (
        <>
          <div className="flex gap-2 mb-3 flex-wrap">
            <Select value={filterMat} onValueChange={setFilterMat}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 w-72" data-testid="almacen-filter-mat">
                <SelectValue placeholder="Material..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100 max-h-72">
                <SelectItem value="__all__">Todos los materiales</SelectItem>
                {materialesConStock.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 w-40">
                <SelectValue placeholder="Tipo..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="entrada">Entradas</SelectItem>
                <SelectItem value="salida">Salidas</SelectItem>
                <SelectItem value="ajuste">Ajustes</SelectItem>
                <SelectItem value="merma">Mermas</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border border-zinc-800 rounded-lg overflow-hidden">
            {movimientos.length === 0 ? (
              <div className="p-12 text-center text-zinc-500">No hay movimientos para los filtros seleccionados.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase">
                  <tr>
                    <th className="text-left p-3 w-36">Fecha</th>
                    <th className="text-left p-3 w-24">Tipo</th>
                    <th className="text-left p-3">Material</th>
                    <th className="text-right p-3 w-24">Cantidad</th>
                    <th className="text-right p-3 w-36">Stock antes → después</th>
                    <th className="text-left p-3 w-48">Motivo / referencia</th>
                    <th className="text-left p-3 w-32">Usuario</th>
                    <th className="text-right p-3 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((m) => {
                    const meta = TIPO_LABELS[m.tipo] || TIPO_LABELS.entrada;
                    const Icon = meta.icon;
                    return (
                      <tr key={m.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/30">
                        <td className="p-3 text-zinc-400 text-xs">
                          {new Date(m.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${meta.color}`}>
                            <Icon className="w-3 h-3" /> {meta.label}
                          </span>
                        </td>
                        <td className="p-3 text-zinc-100">{m.material_nombre || "—"}</td>
                        <td className="p-3 text-right font-mono">{(m.cantidad || 0).toFixed(2)} {m.unidad}</td>
                        <td className="p-3 text-right font-mono text-xs text-zinc-400">
                          {(m.stock_antes || 0).toFixed(2)} → <span className="text-zinc-100">{(m.stock_despues || 0).toFixed(2)}</span>
                        </td>
                        <td className="p-3 text-zinc-300 text-xs">
                          {m.motivo || "—"}
                          {m.referencia_tipo && (
                            <div className="text-[10px] text-amber-400 mt-0.5">
                              ref: {m.referencia_tipo}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-zinc-400 text-xs">{m.usuario_nombre || "auto"}</td>
                        <td className="p-3 text-right">
                          <Button size="sm" variant="ghost"
                                  onClick={async () => {
                                    if (!window.confirm("¿Revertir este movimiento?")) return;
                                    try {
                                      await api.delete(`/almacen/movimientos/${m.id}`);
                                      toast.success("Movimiento revertido");
                                      loadAll();
                                      loadMovs();
                                    } catch (err) {
                                      toast.error(err?.response?.data?.detail || "Error");
                                    }
                                  }}
                                  className="text-rose-400 hover:text-rose-300 h-7 px-2">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      <NewMovimientoDialog open={showNewMov} onOpenChange={setShowNewMov}
                            materiales={materialesConStock}
                            onCreated={() => { loadAll(); if (tab === "movimientos") loadMovs(); }} />
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color = "text-zinc-100" }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center">
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
        <div className={`text-xl font-bold font-mono ${color}`}>{value}</div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center
              ${active ? "border-amber-500 text-amber-400" : "border-transparent text-zinc-400 hover:text-zinc-100"}`}>
      {children}
    </button>
  );
}

function NewMovimientoDialog({ open, onOpenChange, materiales, onCreated }) {
  const [form, setForm] = useState({
    material_id: "", tipo: "entrada", cantidad: "", cantidad_resultante: "",
    motivo: "", coste_unitario: "", proveedor_id: "",
  });
  const [proveedores, setProveedores] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ material_id: "", tipo: "entrada", cantidad: "", cantidad_resultante: "", motivo: "", coste_unitario: "", proveedor_id: "" });
      api.get("/proveedores").then((r) => setProveedores(r.data || [])).catch(() => {});
    }
  }, [open]);

  const submit = async () => {
    if (!form.material_id) return toast.error("Selecciona un material");
    if (form.tipo === "ajuste" && form.cantidad_resultante === "")
      return toast.error("Indica el stock resultante");
    if (form.tipo !== "ajuste" && (!form.cantidad || Number(form.cantidad) <= 0))
      return toast.error("Indica una cantidad > 0");

    setSaving(true);
    try {
      const payload = {
        material_id: form.material_id,
        tipo: form.tipo,
        motivo: form.motivo || null,
      };
      if (form.tipo === "ajuste") {
        payload.cantidad_resultante = Number(form.cantidad_resultante);
      } else {
        payload.cantidad = Number(form.cantidad);
      }
      if (form.tipo === "entrada") {
        if (form.coste_unitario) payload.coste_unitario = Number(form.coste_unitario);
        if (form.proveedor_id && form.proveedor_id !== "__none__") payload.proveedor_id = form.proveedor_id;
      }
      payload.referencia_tipo = "manual";
      const { data } = await api.post("/almacen/movimientos", payload);
      toast.success(`Stock: ${data.stock_antes.toFixed(2)} → ${data.stock_despues.toFixed(2)}`);
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando movimiento");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-amber-400" /> Nuevo movimiento de stock
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Las salidas por producción se generan automáticamente al pasar componentes a estado «en producción».
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Material *</Label>
            <Select value={form.material_id} onValueChange={(v) => setForm({ ...form, material_id: v })}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="almacen-mov-material">
                <SelectValue placeholder="Selecciona..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100 max-h-72">
                {materiales.length === 0 ? (
                  <div className="p-3 text-xs text-zinc-500">
                    Ningún material tiene control de stock activado. Activa el control en cada material desde Materiales.
                  </div>
                ) : (
                  materiales.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.nombre} <span className="text-zinc-500 text-xs">· stock {m.stock_actual?.toFixed(2) ?? "0"} {m.unidad}</span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tipo *</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="almacen-mov-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                  <SelectItem value="entrada">Entrada (compra)</SelectItem>
                  <SelectItem value="salida">Salida (consumo manual)</SelectItem>
                  <SelectItem value="ajuste">Ajuste (inventario)</SelectItem>
                  <SelectItem value="merma">Merma (rotura/pérdida)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.tipo === "ajuste" ? (
              <div>
                <Label className="text-xs">Stock resultante *</Label>
                <Input type="number" step="0.01" value={form.cantidad_resultante}
                       onChange={(e) => setForm({ ...form, cantidad_resultante: e.target.value })}
                       className="bg-zinc-900 border-zinc-800 mt-1" placeholder="0.00"
                       data-testid="almacen-mov-cantidad" />
              </div>
            ) : (
              <div>
                <Label className="text-xs">Cantidad *</Label>
                <Input type="number" step="0.01" value={form.cantidad}
                       onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
                       className="bg-zinc-900 border-zinc-800 mt-1" placeholder="0.00"
                       data-testid="almacen-mov-cantidad" />
              </div>
            )}
          </div>

          {form.tipo === "entrada" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Coste unitario (€)</Label>
                <Input type="number" step="0.01" value={form.coste_unitario}
                       onChange={(e) => setForm({ ...form, coste_unitario: e.target.value })}
                       className="bg-zinc-900 border-zinc-800 mt-1" />
              </div>
              <div>
                <Label className="text-xs">Proveedor</Label>
                <Select value={form.proveedor_id || "__none__"}
                        onValueChange={(v) => setForm({ ...form, proveedor_id: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100 max-h-60">
                    <SelectItem value="__none__">—</SelectItem>
                    {proveedores.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Motivo / nota</Label>
            <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                   className="bg-zinc-900 border-zinc-800 mt-1"
                   placeholder="Ej. Compra a Salgar 23/05, inventario físico…" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="bg-transparent border-zinc-700">Cancelar</Button>
          <Button onClick={submit} disabled={saving}
                  data-testid="almacen-mov-submit"
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : "Guardar movimiento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
